import TelemedicineContext from '../context/telemedicine';
import { listFiles } from '../services/telemedicine-file';
import { formatFileSize } from '../utils/uploaded-files';

import { useCallback, useContext, useEffect, useState } from 'react';

const toListedFile = file => ({
	id: file.id,
	name: file.name,
	sizeLabel: formatFileSize(file.size ?? 0)
});

/**
 * The files the patient sent in this room, read from the API.
 *
 * The waiting room and the call each used to keep their own list in local state, so whatever
 * the patient attached before joining vanished from the call's panel (the doctor, reading the
 * API, still saw it) - and a reload emptied both. Loading from the room keeps one source of
 * truth; files this screen just uploaded are kept until the next read confirms them.
 */
const usePatientFiles = () => {
	const { telemedicine } = useContext(TelemedicineContext);

	const [files, setFiles] = useState([]);

	const roomId = telemedicine?.id;
	const patientId = telemedicine?.patient_id;

	const refresh = useCallback(async () => {
		if (!roomId || !patientId) {
			return;
		}

		const { data } = await listFiles({ patientId, roomId });

		if (!Array.isArray(data)) {
			return;
		}

		// The API lists newest first; the panels show them in the order they were sent.
		const listed = [...data].reverse().map(toListedFile);
		const listedIds = new Set(listed.map(file => file.id));

		setFiles(current => [...listed, ...current.filter(file => !listedIds.has(file.id))]);
	}, [patientId, roomId]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { files, refresh, setFiles };
};

export default usePatientFiles;
